import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosClient from "../api/axiosClient";
import { on } from "../signalr/chatHub";
import { timeAgo } from "../utils/timeAgo";
import { getNotificationMeta, getNotificationLink } from "../utils/notificationMeta";
import { useLangPrefix } from "../i18n";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef();
  const navigate = useNavigate();
  const lp = useLangPrefix();

  const load = async () => {
    try {
      const { data } = await axiosClient.get("/notification");
      setNotifications(data.slice(0, 6));
      setUnreadCount(data.filter((n) => !n.isRead).length);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    load();
    const off = on("ReceiveNotification", (notif) => {
      setNotifications((prev) => [notif, ...prev].slice(0, 6));
      setUnreadCount((c) => c + 1);
    });
    return off;
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleOpen = async () => {
    setOpen((v) => !v);
    if (!open && unreadCount > 0) {
      await axiosClient.post("/notification/read-all");
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    }
  };

  const handleClick = (n) => {
    setOpen(false);
    const link = getNotificationLink(n, lp);
    if (link) navigate(link);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={handleOpen} className="relative p-2 rounded-full hover:bg-gray-50" aria-label="الإشعارات">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M13.73 21a2 2 0 01-3.46 0" strokeLinecap="round" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -end-0.5 bg-primary-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute end-0 mt-2 w-80 max-h-96 overflow-y-auto card z-50">
          <div className="p-3 border-b border-gray-100 font-bold text-gray-700">الإشعارات</div>
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">لا توجد إشعارات بعد</div>
          ) : (
            notifications.map((n) => {
              const meta = getNotificationMeta(n.type, n.content);
              const Icon = meta.icon;
              return (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full flex items-center gap-3 text-right px-4 py-3 border-b border-gray-50 hover:bg-gray-50 ${
                    !n.isRead ? "bg-primary-50/40" : ""
                  }`}
                >
                  <span className={`w-9 h-9 rounded-lg ${meta.bg} ${meta.color} flex items-center justify-center shrink-0`}>
                    <Icon width={16} height={16} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <p className="text-gray-700 text-sm truncate">{n.content}</p>
                    <span className="text-xs text-gray-500">{timeAgo(n.createdAt)}</span>
                  </span>
                </button>
              );
            })
          )}
          <button
            onClick={() => {
              setOpen(false);
              navigate(`${lp}/notifications`);
            }}
            className="w-full text-center py-3 text-sm font-bold text-primary-600 hover:bg-primary-50 transition-colors"
          >
            عرض كل الإشعارات
          </button>
        </div>
      )}
    </div>
  );
}
